// @ts-nocheck
import { ReactComponent as CloudRaining } from "../../assets/icons/cloud-raining-05.svg";
import { ReactComponent as WindIcon } from "../../assets/icons/wind-02.svg";
import { ReactComponent as SunIcon } from "../../assets/icons/sun.svg";
import { ReactComponent as HurricaneIcon } from "../../assets/icons/hurricane-02.svg";
import { ReactComponent as TemperatureIcon } from "../../assets/icons/thermometer-03.svg";
import { ReactComponent as HumidityIcon } from "../../assets/icons/droplets-01.svg";
import { ReactComponent as SoilMoistureIcon } from "../../assets/icons/droplets-02.svg";
import { ReactComponent as SnowflakeIcon } from "../../assets/icons/snowflake-01.svg";
import { ReactComponent as DivideIcon } from "../../assets/icons/divide-02.svg";
import { buildComposedForecastSchemaEntries, defaultComposedForecastKey } from "../../utils/composedForecastDashboardUtils";

/**
 * Builds the complete forecast schema with all available forecast types
 * @param {Object} selectedAsset - The current asset
 * @param {Object} processedModelData - Processed model data for custom forecasts
 * @param {Object} [composedForecastData] - Optional cached CombinedPreviewData by composed-forecast id.
 *   Not required for the dropdown entries to appear (those are driven by
 *   selectedAsset.linkedComposedForecasts); only used downstream to know
 *   whether data has been fetched yet.
 * @returns {Object} Complete forecast schema
 */
export const buildForecastSchema = (selectedAsset, processedModelData, composedForecastData) => {
  const tempForecastSchema = {
    Precipitation: {
      name: "Precipitation",
      Icon: CloudRaining,
      variations: [
        { name: "Hourly", key: "Hourly" },
        { name: "Cumulative", key: "Cumulative" },
      ],
    },
    Wind80: {
      name: "Wind (80m)",
      Icon: WindIcon,
      variations: [
        ...(selectedAsset?.windCurve ? [{ name: "Power", key: "Power" }] : []),
        { name: "Speed", key: "Speed" },
        { name: "Direction", key: "Direction" },
      ],
    },
    Wind10: {
      name: "Wind (10m)",
      Icon: WindIcon,
      variations: [
        { name: "Speed", key: "Speed" },
        { name: "Direction", key: "Direction" },
      ],
    },
    Solar: {
      name: "Solar",
      Icon: SunIcon,
      variations: [
        ...(selectedAsset?.solarCurve ? [{ name: "Power", key: "Power" }] : []),
        { name: "Short Wave", key: "sWave" },
        { name: "Long Wave", key: "lWave" },
      ],
    },
    Temperature: {
      name: "Temperature",
      Icon: TemperatureIcon,
      variations: [{ name: "Dry Bulb", key: "Hourly" }],
    },
    Humidity: {
      name: "Humidity",
      Icon: HumidityIcon,
      variations: [{ name: "Relative Humidity", key: "Hourly" }],
    },
    SoilMoisture: {
      name: "Soil Moisture",
      Icon: DivideIcon,
      variations: [{ name: "Soil Moisture", key: "Hourly" }],
    },
    SnowAcc: {
      name: "Snow Water",
      Icon: SnowflakeIcon,
      variations: [{ name: "Snow Water Equivalent", key: "Hourly" }],
    },
  };

  // Add processed models to the schema
  if (processedModelData && selectedAsset?.processedModels?.items) {
    selectedAsset.processedModels.items.forEach((element) => {
      const variations = processedModelData?.[element?.routeShort]?.models;
      if (variations) {
        const variationKeys = Object.keys(variations).map((key) => ({
          key,
          name: variations[key].variation,
        }));

        tempForecastSchema[`processed-${element.routeShort}`] = {
          name: element.label,
          Icon: HurricaneIcon,
          variations: variationKeys,
        };
      }
    });
  }

  // Add composed forecasts attached to this asset. Driven purely off
  // selectedAsset.linkedComposedForecasts so the dropdown entry appears
  // before any chart data has been fetched (fetch happens lazily on select).
  const composedEntries = buildComposedForecastSchemaEntries(
    selectedAsset,
    HurricaneIcon
  );
  Object.assign(tempForecastSchema, composedEntries);

  return tempForecastSchema;
};

/**
 * Stored in an asset's displayedChannels to hide every weather forecast type. An empty list
 * can't say this — it already means "show all" — so "none" gets its own marker.
 */
export const DISPLAYED_CHANNELS_NONE = "NONE";

/**
 * True when the asset is set to show no weather forecast types
 * @param {Array<string>|null} displayedChannels
 * @returns {boolean}
 */
export const isWeatherHidden = (displayedChannels) =>
  Array.isArray(displayedChannels) && displayedChannels.includes(DISPLAYED_CHANNELS_NONE);

/**
 * True when the asset has a Custom View (active composed forecast) or a V1 processed model attached
 * @param {Object} asset
 * @returns {boolean}
 */
export const hasAttachedForecasts = (asset) =>
  Object.keys(buildComposedForecastSchemaEntries(asset, null)).length > 0 ||
  (asset?.processedModels?.items?.length ?? 0) > 0;

/**
 * The forecast an asset opens on because of what's attached to it: its default Custom View (composed
 * forecast), else its first V1 processed model. Null when it has neither — the caller then keeps the
 * user's current weather type.
 * @param {Object} asset
 * @returns {string|null}
 */
export const assetDefaultForecastKey = (asset) => {
  const composedKey = defaultComposedForecastKey(asset);
  if (composedKey) return composedKey;
  const routeShort = asset?.processedModels?.items?.[0]?.routeShort;
  return routeShort ? `processed-${routeShort}` : null;
};

// The group map view's forecast keys, mapped to the displayedChannels keys the asset form saves
const MAP_VIEW_CHANNEL_KEYS = {
  precipitation: "Precipitation",
  wind10m: "Wind10",
  wind80m: "Wind80",
  windPower: "Wind80",
  temperature: "Temperature",
  humidity: "Humidity",
  solarS: "Solar",
  solarL: "Solar",
  soilMoisture: "SoilMoisture",
  snowWater: "SnowAcc",
};

/**
 * Whether a group map view forecast type (e.g. "wind10m") should be offered, given displayedChannels
 * @param {Array<string>|null} displayedChannels
 * @param {string} mapViewKey
 * @returns {boolean}
 */
export const isMapViewForecastDisplayed = (displayedChannels, mapViewKey) => {
  if (!displayedChannels || displayedChannels.length === 0) return true;
  if (isWeatherHidden(displayedChannels)) return false;
  return displayedChannels.includes(MAP_VIEW_CHANNEL_KEYS[mapViewKey] ?? mapViewKey);
};

/**
 * Filters forecast schema based on displayedChannels
 * @param {Object} fullSchema - Complete forecast schema
 * @param {Array<string>} displayedChannels - Array of channel keys to display; [DISPLAYED_CHANNELS_NONE] hides all weather types
 * @returns {Object} Filtered forecast schema
 */
export const filterForecastSchema = (fullSchema, displayedChannels) => {
  // If no displayedChannels or empty array, return full schema
  if (!displayedChannels || displayedChannels.length === 0) {
    return fullSchema;
  }
  // The NONE marker matches no channel below, so only processed models and composed forecasts are kept

  const filteredSchema = {};
  const channelMap = {
    Precipitation: "Precipitation",
    Wind80: "Wind80",
    Wind10: "Wind10",
    Solar: "Solar",
    Temperature: "Temperature",
    Humidity: "Humidity",
    SoilMoisture: "SoilMoisture",
    SnowAcc: "SnowAcc",
  };

  displayedChannels.forEach((channel) => {
    const schemaKey = channelMap[channel];
    if (schemaKey && fullSchema[schemaKey]) {
      filteredSchema[schemaKey] = fullSchema[schemaKey];
    }

    // Also include processed models if they match
    if (channel.startsWith("processed-") && fullSchema[channel]) {
      filteredSchema[channel] = fullSchema[channel];
    }
  });

  // IMPORTANT: Always include all processed models AND composed forecasts
  // from fullSchema, even if not explicitly in displayedChannels — those
  // entries are asset-attachment-driven, not channel-toggle-driven.
  Object.keys(fullSchema).forEach((key) => {
    if (key.startsWith("processed-") || key.startsWith("composed-")) {
      filteredSchema[key] = fullSchema[key];
    }
  });

  return filteredSchema;
};

/**
 * Gets the first available forecast type from a schema
 * @param {Object} schema - Forecast schema
 * @returns {string|null} First forecast key or null if schema is empty
 */
export const getFirstAvailableForecast = (schema) => {
  const keys = Object.keys(schema);
  return keys.length > 0 ? keys[0] : null;
};

/**
 * Checks if a forecast type is available in the schema
 * @param {Object} schema - Forecast schema
 * @param {string} forecastType - Forecast type to check
 * @returns {boolean} True if forecast type exists in schema
 */
export const isForecastAvailable = (schema, forecastType) => {
  return schema && schema.hasOwnProperty(forecastType);
};

/**
 * Gets a smart default forecast type based on schema and preferences
 * @param {Object} schema - Forecast schema (filtered or full)
 * @param {string} currentForecast - Currently selected forecast
 * @param {Object} selectedAsset - The current asset
 * @returns {string} The forecast type to use
 */
export const getSmartDefaultForecast = (schema, currentForecast, selectedAsset) => {
  // A linked composed forecast (the one marked default, else the first) always wins
  const composedKey = defaultComposedForecastKey(selectedAsset);
  if (composedKey && isForecastAvailable(schema, composedKey)) {
    return composedKey;
  }

  // Then the asset's first processed model
  if (selectedAsset?.processedModels?.items?.[0]?.routeShort) {
    const processedKey = `processed-${selectedAsset.processedModels.items[0].routeShort}`;
    if (isForecastAvailable(schema, processedKey)) {
      return processedKey;
    }
  }

  // If current forecast is available in schema, use it
  if (isForecastAvailable(schema, currentForecast)) {
    return currentForecast;
  }

  // Then Temperature, the default weather type (the current forecast may be the previous asset's model)
  if (isForecastAvailable(schema, "Temperature")) {
    return "Temperature";
  }

  // Fall back to first available forecast
  const firstAvailable = getFirstAvailableForecast(schema);

  // If still nothing available, return Wind80 as ultimate fallback
  return firstAvailable || "Wind80";
};

/**
 * Gets the default variation for a forecast type
 * @param {string} forecastType - The forecast type
 * @param {Object} schema - Forecast schema
 * @param {Object} selectedAsset - The current asset
 * @returns {string} Default variation key
 */
export const getDefaultVariation = (forecastType, schema, selectedAsset) => {
  // Get the forecast type from the schema
  const forecastSchema = schema[forecastType];

  if (!forecastSchema || !forecastSchema.variations || forecastSchema.variations.length === 0) {
    return "Speed"; // Ultimate fallback
  }

  // For Wind80 with power curve, prefer Power variation if it exists
  if (forecastType === "Wind80" && selectedAsset?.windCurve) {
    const powerVariation = forecastSchema.variations.find(v => v.key === "Power");
    if (powerVariation) {
      return "Power";
    }
  }

  // For all types (including processed models), return the first variation from the schema
  // This ensures Humidity gets "Hourly", Temperature gets "Hourly", etc.
  return forecastSchema.variations[0]?.key || "Speed";
};
